import restUrlData from './rest-url-data';
import * as R from 'ramda';
import { getKey, getValue, valueIsNotNull, isMinimumOrMaximum, isNotNullOrEmpty, isNullOrEmpty, isMinimum, concatProps } from './url-utils';
const NOT_FIXED_STRING_FIELDS = ['keyWord', 'comissionId'];
const keys = restUrlData.keys;
const values = restUrlData.values;
const stateToUrlStringFixed = R.cond([
	[
		valueIsNotNull,
		R.applySpec({
			key: getKey(keys),
			value: getValue(values)
		})
	]
]);

const stateToUrlString = R.cond([
	[
		valueIsNotNull,
		R.applySpec({
			key: getKey(keys),
			value: R.nthArg(1)
		})
	]
]);

const stateToUrlNumeric = R.cond([
	[
		valueIsNotNull,
		R.applySpec({
			key: getKey(keys),
			value: R.nthArg(1)
		})
	]
]);
const STRING_VALUES_PARAMS = ['floorsInBuilding'];
const getPostfixForPrice = saleType => {
	const POSTFIXES = {
		forSell: 'm',
		forRent: 'k'
	};
	return R.prop(saleType, POSTFIXES);
};
const stateToUrlMinimum = (key, value, params) => {
	const valueName = R.replace('Minimum', '', key);
	const maxName = `${valueName}Maximum`;
	const maxValue = params[maxName];
	if (STRING_VALUES_PARAMS.includes(valueName)) {
		const possibleValues = getKey(values)(valueName);
		const fromIndex = R.findIndex(item => R.equals(item, value), possibleValues);
		const toIndex = R.findIndex(item => R.equals(item, maxValue), possibleValues);
		if (toIndex == -1 && fromIndex == -1) {
			return;
		} else {
			const realFromIndex = R.ifElse(R.equals(-1), R.always(0), R.identity)(fromIndex);
			const lengthOfPossibleValues = R.length(possibleValues);
			const realToIndex = R.ifElse(R.equals(-1), R.always(lengthOfPossibleValues), R.identity)(toIndex);
			const restValues = R.pipe(R.slice(realFromIndex, realToIndex + 1), concatProps)(possibleValues);
			return {
				key: keys[valueName],
				value: restValues
			};
		}
	} else {
		const convertedValue = STRING_VALUES_PARAMS.includes(valueName) ? values[valueName][value] : value;
		const convertedMaxValue = STRING_VALUES_PARAMS.includes(valueName) ? values[valueName][maxValue] : maxValue;
		const isNotPlusValue = R.complement(R.pipe(R.defaultTo(''), R.last, R.equals('+')));
		const isPrice = () => R.equals(valueName, 'price');
		const postfix = R.ifElse(isPrice, getPostfixForPrice, R.always(''))(params.saleType);
		if (R.and(R.and(isNotNullOrEmpty(maxValue), isNotNullOrEmpty(value)), isNotPlusValue(maxValue))) {
			return {
				key: keys[valueName],
				value: `${convertedValue}${postfix}~${convertedMaxValue}${postfix}`
			};
		} else if (isNotNullOrEmpty(value)) {
			return {
				key: keys[valueName],
				value: `${convertedValue}${postfix}~`
			};
		}
	}
};
const stateToUrlMaximum = (key, value, params) => {
	const valueName = R.replace('Maximum', '', key);
	const minName = `${valueName}Minimum`;
	const minValue = params[minName];
	const isPrice = () => R.equals(valueName, 'price');
	const postfix = R.ifElse(isPrice, getPostfixForPrice, R.always(''))(params.saleType);
	const isNotStringValue = R.not(STRING_VALUES_PARAMS.includes(valueName));
	if (isNullOrEmpty(minValue) && isNotNullOrEmpty(value) && isNotStringValue) {
		return {
			key: keys[valueName],
			value: `~${value}${postfix}`
		};
	}
};
const stateToUrlRange = R.ifElse(isMinimum, stateToUrlMinimum, stateToUrlMaximum);
const stateToUrlSelectedTags = value => {
	const mergedValue = R.pipe(R.pluck('value'), concatProps);
	const mergedType = R.pipe(R.pluck('type'), concatProps);
	const isMNumber = R.pipe(R.prop('type'), R.equals('MID'));
	const mNumber = R.prop('value', R.find(isMNumber, value));
	if (mNumber) {
		return {
			key: 'comissionId',
			value: mNumber
		};
	} else {
		const location = mergedValue(value);
		const locationTypes = mergedType(value);

		return [
			{
				key: 'location',
				value: location
			},
			{
				key: 'locationTypes',
				value: locationTypes
			}
		];
	}
};

const stateToUrlArrayGeneral = R.curry((key, value) => {
	const getTranslatedValue = getValue(values)(key);
	const mapTranslated = R.map(getTranslatedValue);
	const mergedValue = R.pipe(mapTranslated, R.flatten, R.uniq, R.join('|'));
	return {
		key: keys[key],
		value: mergedValue(value)
	};
});

const stateToUrlArray = R.ifElse(R.equals('selectedTags'), R.pipe(R.nthArg(1), stateToUrlSelectedTags), stateToUrlArrayGeneral);
const stateToUrlBoolean = R.ifElse(
	R.pipe(R.nthArg(1), R.equals(true)),
	R.applySpec({
		key: getKey(keys),
		value: getKey(values)
	}),
	R.always(null)
);

const stateToUrlOrder = value => {
	const isAsc = R.endsWith('Asc', value);
	const fieldName = R.pipe(R.replace('Desc', ''), R.replace('Asc', ''))(value);
	const orderDirection = { key: 'orderDirection', value: isAsc ? 'asc' : 'desc' };
	const orderBy = { key: 'orderBy', value: fieldName };
	return [orderDirection, orderBy];
};
const stateToUrl = (key, value, params) => {
	if (R.equals('order', key)) {
		return stateToUrlOrder(value);
	} else if (isMinimumOrMaximum(key)) {
		return stateToUrlRange(key, value, params);
	} else if (R.is(Boolean, value)) {
		return stateToUrlBoolean(key, value);
	} else if (typeof value == 'string') {
		return R.includes(key, NOT_FIXED_STRING_FIELDS) ? stateToUrlString(key, value) : stateToUrlStringFixed(key, value);
	} else if (typeof value == 'number') {
		return stateToUrlNumeric(key, value, params);
	} else if (Array.isArray(value) && R.length(value)) {
		return stateToUrlArray(key, value);
	}
};
export default {
	createUrlFromState(params) {
		const paramsArray = R.toPairs(params);
		const restUrlParams = R.reduce(
			(acc, item) => {
				const convertedValue = stateToUrl(item[0], item[1], params);
				//TODO Ramda
				if (Array.isArray(convertedValue)) {
					R.forEach(arrItem => {
						acc[arrItem.key] = arrItem.value;
					}, convertedValue);
				} else if (R.not(R.isNil(convertedValue))) {
					acc[convertedValue.key] = convertedValue.value;
				}
				return acc;
			},
			{ pageSize: 8 },
			paramsArray
		);
		const getComissionId = R.prop('comissionId');
		return R.ifElse(getComissionId, R.pipe(getComissionId, R.objOf('comissionId')), R.identity)(restUrlParams);
	}
};
